import { Box, TextField } from '@mui/material';
import { ChangeEvent, FocusEvent, useContext, useEffect, useState } from 'react';

import { LocaleContext } from '@/Contexts/LocaleContext';
import { EventProps } from '@/models/Event';
import { QuestionProps } from '@/models/Question';
import { stringHasValue } from '@/utils/stringUtil';

import CharacterCount from './CharacterCount';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';

type EventDescriptionProps = {
  question: QuestionProps;
  eventInfo: EventProps;
  onEventChange: (updatedEvent: EventProps) => void;
  selectOnFocus: (e: FocusEvent<HTMLInputElement>) => void;
};

export default function EventDescription({
  question,
  eventInfo,
  onEventChange,
  selectOnFocus,
}: EventDescriptionProps) {
  const { localeLanguageCode, translatedText } = useContext(LocaleContext);
  const { canEdit } = useContext(CurrentApplicationContext);
  const [description, setDescription] = useState<string | null>(null);
  const MAX_DESCRIPTION_LENGTH = 1000;

  useEffect(() => {
    if (eventInfo != null) {
      setDescription(eventInfo.description ?? null);
    }
  }, [eventInfo]);

  const inputChanged = (value: string) => {
    onEventChange({
      ...eventInfo,
      description: value,
    });
  };

  if (!stringHasValue(localeLanguageCode)) return <></>;

  const localeCode = localeLanguageCode;
  const eventTypeLabel = question?.eventType?.nameTranslations?.[localeCode] ?? '';

  return (
    <Box>
      <TextField
        fullWidth
        label={
          eventTypeLabel.includes('Training')
            ? 'Topics Covered'
            : `${translatedText?.event?.summary} ${eventTypeLabel}`
        }
        required={question?.questionNeeded}
        disabled={!canEdit}
        value={stringHasValue(description) ? description : ''}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          const v: string = e.target.value;

          setDescription(v);
          inputChanged(v);
        }}
        onFocus={selectOnFocus}
        multiline
        minRows={3}
        error={!stringHasValue(description)}
        slotProps={{
          htmlInput: { maxLength: MAX_DESCRIPTION_LENGTH },
        }}
      />
      <CharacterCount text={description} max={MAX_DESCRIPTION_LENGTH} visible={canEdit} />
    </Box>
  );
}
